import { siesaGet } from './SiesaProxy';
/**
 * Servicio para consumir pedidos desde la API SIESA
 * Endpoints:
 * - {{APISIESA_SANDBOX}}crm/pending-orders?seller_id=xxxxx
 * - {{APISIESA_SANDBOX}}crm/get-order/{document_number}
 */

import { searchSiesaItems } from './OrderAssemblyService';

// Interfaces basadas en la estructura de respuesta de la API SIESA
export interface OrderItem {
  item_extension: string;
  quantity_ordered: number;
  quantity_pending: number;
  quantity_invoiced: number;
  stock_quantity: number;
  unit_price: number;
  subtotal_value: number;
  gross_value: number;
  average_cost_per_unit: number;
  standard_cost: number;
  average_cost: number;
  notes?: string;
  description?: string;
  item_description?: string;
  item_short_description?: string;
  /**
   * Referencia "corta"/externa del ítem en SIESA (f120_descripcion_corta): la
   * que reconoce el cliente (p. ej. D46003-905 para la referencia interna
   * 13001822-K). Se guarda tal cual, sin mezclarla con la descripción, porque
   * el documento del pedido las muestra en columnas distintas.
   */
  short_reference?: string;
}

export interface OrderReference {
  reference: string;
  description: string;
  item_short_description: string;
  /** Ver OrderItem.short_reference. */
  short_reference?: string;
  measure_unit: string;
  notes?: string;
  items: OrderItem[];
}

export interface PendingOrder {
  document_number: string;
  purchase_order: string;
  document_date: string;
  delivery_date: string;
  client: string;
  client_name: string;
  client_branch: string;
  seller: string;
  seller_name: string;
  warehouse_name: string;
  document_status: string;
  notes?: string;
  observations?: string;
  document_subtotal_value?: number;
  document_discount_value?: number;
  document_tax_value?: number;
  document_total_value?: number;
  references: OrderReference[];
}

// Interfaz simplificada para la tabla de MainPage
export interface SimplifiedPendingOrder {
  id: string;
  orderNumber: string;  // Número de pedido formateado
  document_number: string;
  order_type?: string;
  purchase_order: string;
  client: string;
  client_name: string;
  client_nit?: string;
  client_branch?: string;
  branch?: string;
  branch_name?: string;
  contact?: string;
  seller: string;
  seller_name: string;
  document_type?: string;
  document_date: string;
  creation_date?: string;
  delivery_date: string;
  due_date?: string;
  document_status: string;
  status: string;  // Estado simplificado
  observations?: string;
  notes?: string;
  payment_condition?: string;
  warehouse_name?: string;
  document_subtotal_value?: number;
  document_discount_value?: number;
  document_tax_value?: number;
  document_total_value?: number;
  total_items: number;
  total_quantity: number;
  total_value: number;
  references: OrderReference[];
}

// Las consultas a SIESA van por el servidor del CRM (ver SiesaProxy.ts). Aquí ya
// no hay URL ni token: el del ERP se quedó del lado del servidor.

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const withTimeoutFallback = async <T,>(
  promise: Promise<T>,
  timeoutMs: number,
  fallbackFactory: () => T,
): Promise<T> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      promise,
      new Promise<T>((resolve) => {
        timeoutHandle = setTimeout(() => resolve(fallbackFactory()), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }
};

const runWithConcurrency = async <T,>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> => {
  const size = Math.max(1, Math.min(concurrency, items.length || 1));
  let cursor = 0;
  const runners = Array.from({ length: size }, async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      await worker(items[index], index);
    }
  });
  await Promise.all(runners);
};

const isPlaceholderDescription = (reference: string, value: unknown): boolean => {
  const text = String(value || '').trim();
  if (!text) return true;

  const normalized = text.toUpperCase();
  const normalizedReference = String(reference || '').trim().toUpperCase();

  if (normalizedReference && normalized === normalizedReference) return true;
  return normalized === '-' || normalized === 'N/A' || normalized === 'NA';
};

const normalizeDocumentForMatch = (value: string): string =>
  String(value || '').replace(/^0+/, '').trim().toUpperCase();

const extractDocumentDigits = (value: string): string =>
  String(value || '').replace(/\D/g, '').replace(/^0+/, '');

const buildDocumentCandidates = (
  documentNumber: string,
  options?: { fastPath?: boolean },
): string[] => {
  const raw = String(documentNumber || '').trim();
  if (!raw) return [];

  const upper = raw.toUpperCase();
  const noSpaces = upper.replace(/\s+/g, '');
  const normalized = noSpaces.replace(/_/g, '-');
  const compact = normalized.replace(/[^A-Z0-9]/g, '');
  const digits = extractDocumentDigits(normalized);

  const candidates = new Set<string>();

  const prefixedMatch = compact.match(/^([A-Z]+)(\d+)$/);
  if (prefixedMatch) {
    const prefix = prefixedMatch[1];
    const number = prefixedMatch[2].replace(/^0+/, '') || prefixedMatch[2];
    const canonical = `${prefix}-${number}`;
    candidates.add(canonical);
    if (raw.includes('-')) candidates.add(raw);
    if (upper.includes('-')) candidates.add(upper);
    if (normalized.includes('-')) candidates.add(normalized);
    if (options?.fastPath) {
      return Array.from(
        new Set<string>([
          canonical,
          normalized.includes('-') ? normalized : '',
          raw.includes('-') ? raw : '',
        ].filter(Boolean)),
      );
    }
  } else {
    [raw, upper, normalized, compact].filter(Boolean).forEach((candidate) => candidates.add(candidate));
  }

  if (digits) {
    candidates.add(digits);
    if (normalized.includes('-')) {
      const [prefixRaw] = normalized.split('-', 1);
      const prefix = String(prefixRaw || '').replace(/[^A-Z]/g, '');
      if (prefix) {
        candidates.add(`${prefix}-${digits}`);
      }
    } else {
      // Common document prefixes used by ERP orders.
      ['CPV', 'CPT'].forEach((prefix) => {
        candidates.add(`${prefix}-${digits}`);
      });
    }
  }

  const list = Array.from(candidates).filter(Boolean);
  return options?.fastPath ? list.slice(0, 6) : list;
};

const pickOrderFromPayload = (payload: any): PendingOrder | null => {
  const toNumber = (value: unknown, fallback = 0): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const pickMeaningfulText = (reference: string, ...candidates: unknown[]): string => {
    const normalizedReference = String(reference || '').trim().toUpperCase();
    for (const candidate of candidates) {
      const text = String(candidate || '').trim();
      if (!text) continue;
      if (normalizedReference && text.toUpperCase() === normalizedReference) continue;
      return text;
    }
    return '';
  };

  const mapGetOrderPayload = (raw: any): PendingOrder | null => {
    if (!raw || typeof raw !== 'object') return null;

    const documentNumber = String(raw.document_number || raw.document || '').trim();
    if (!documentNumber) return null;

    const rawItems = Array.isArray(raw.items) ? raw.items : [];
    const references: OrderReference[] = rawItems.map((item: any) => {
      const itemReference = String(item?.reference || '').trim();
      const measureUnit = String(item?.measure_unit || 'UND').trim() || 'UND';
      const extensions = Array.isArray(item?.extensions) ? item.extensions : [];

      const mappedItems: OrderItem[] = (extensions.length > 0 ? extensions : [item]).map((extension: any) => {
        // La descripcion real del item (f120_descripcion) va primero: la
        // referencia corta/externa (f120_descripcion_corta) viaja aparte en
        // short_reference y tiene su propia columna en el documento, asi que ya
        // no hace falta colarla aqui salvo que no haya descripcion.
        const itemDescription = pickMeaningfulText(
          itemReference,
          extension?.item_description,
          extension?.description,
          item?.item_description,
          item?.description,
          extension?.item_short_description,
          item?.item_short_description,
        );

        return {
          item_extension: String(extension?.item_extension || '').trim(),
          quantity_ordered: toNumber(extension?.quantity_ordered ?? item?.quantity_ordered, 0),
          quantity_pending: toNumber(extension?.quantity_pending ?? item?.quantity_pending, 0),
          quantity_invoiced: 0,
          stock_quantity: 0,
          unit_price: toNumber(extension?.unit_price ?? item?.unit_price, 0),
          subtotal_value: toNumber(extension?.subtotal ?? item?.subtotal, 0),
          gross_value: toNumber(extension?.value ?? item?.value ?? extension?.subtotal ?? item?.subtotal, 0),
          average_cost_per_unit: 0,
          standard_cost: 0,
          average_cost: 0,
          notes: String(extension?.notes || item?.notes || '').trim() || undefined,
          description: itemDescription || undefined,
          item_description: itemDescription || undefined,
          item_short_description: itemDescription || undefined,
          short_reference:
            String(extension?.item_short_description || item?.item_short_description || '').trim() ||
            undefined,
        };
      });

      const referenceDescription = pickMeaningfulText(
        itemReference,
        item?.description,
        item?.item_description,
        item?.item_short_description,
      );

      return {
        reference: itemReference,
        description: referenceDescription,
        item_short_description: pickMeaningfulText(
          itemReference,
          item?.item_short_description,
          item?.description,
          item?.item_description,
        ),
        short_reference: String(item?.item_short_description || '').trim() || undefined,
        measure_unit: measureUnit,
        notes: String(item?.notes || '').trim() || undefined,
        items: mappedItems,
      };
    });

    const totals = raw?.total_general || {};

    return {
      document_number: documentNumber,
      purchase_order: String(raw.purchase_order || '').trim(),
      document_date: String(raw.document_date || '').trim(),
      delivery_date: String(raw.delivery_date || '').trim(),
      client: String(raw.client || raw.client_id || '').trim(),
      client_name: String(raw.client_name || '').trim(),
      client_branch: String(raw.client_branch || raw.branch_name || raw.contact_name || '').trim(),
      seller: String(raw.seller || raw.seller_id || '').trim(),
      seller_name: String(raw.seller_name || '').trim(),
      warehouse_name: String(raw.warehouse_name || '').trim(),
      document_status: String(raw.document_status || raw.status || '').trim(),
      notes: String(raw.notes || raw.observations || '').trim() || undefined,
      observations: String(raw.observations || raw.notes || '').trim() || undefined,
      document_subtotal_value: toNumber(raw.document_subtotal_value ?? totals.subtotal, 0),
      document_discount_value: toNumber(raw.document_discount_value ?? raw.discount ?? 0, 0),
      document_tax_value: toNumber(raw.document_tax_value ?? totals.taxes, 0),
      document_total_value: toNumber(raw.document_total_value ?? totals.value, 0),
      references,
    };
  };

  if (!payload) return null;
  if (Array.isArray(payload)) return mapGetOrderPayload(payload.find((row) => row && typeof row === 'object') || null);
  if (Array.isArray(payload?.data)) return mapGetOrderPayload(payload.data.find((row: any) => row && typeof row === 'object') || null);
  if (payload?.data && typeof payload.data === 'object') return mapGetOrderPayload(payload.data);
  if (payload?.order && typeof payload.order === 'object') return mapGetOrderPayload(payload.order);
  if (typeof payload === 'object') return mapGetOrderPayload(payload);
  return null;
};

const fetchOrderByDocumentFromSiesa = async (
  documentCode: string,
  sellerId?: string,
): Promise<PendingOrder | null> => {
  const expectedNormalized = normalizeDocumentForMatch(documentCode);
  const expectedDigits = extractDocumentDigits(documentCode);

  try {
    const payload = await siesaGet('get-order', {
      document: documentCode,
      seller_id: sellerId,
    });
    const order = pickOrderFromPayload(payload);
    if (!order) return null;

    const candidateNormalized = normalizeDocumentForMatch(order.document_number || '');
    const candidateDigits = extractDocumentDigits(order.document_number || '');
    if (
      !expectedNormalized ||
      candidateNormalized === expectedNormalized ||
      (expectedDigits && candidateDigits === expectedDigits)
    ) {
      return order;
    }

    // The endpoint is document-specific, but if payload shape differs, still accept valid order object.
    if (candidateNormalized) return order;
    return null;
  } catch (error) {
    console.debug('[PendingOrdersService] get-order request failed:', documentCode, error);
    return null;
  }
};

/**
 * Obtener pedidos pendientes desde SIESA
 * @param sellerId - ID del vendedor (cédula)
 * @returns Lista de pedidos pendientes
 */
export const fetchPendingOrders = async (sellerId?: string): Promise<PendingOrder[]> => {
  const normalizedSellerId = String(sellerId || '').trim();
  try {
    // El 404 de apiSiesa ("este vendedor no tiene pendientes") lo traduce el
    // proxy a lista vacía, así que aquí ya no hay que interpretar el mensaje.
    const data = await siesaGet('crm/pending-orders', { seller_id: normalizedSellerId });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('[PendingOrdersService] Error:', error);
    throw error;
  }
};

/**
 * Transformar pedidos pendientes a formato simplificado para la tabla
 */
export const transformToSimplified = (orders: PendingOrder[]): SimplifiedPendingOrder[] => {
  const safeOrders = Array.isArray(orders) ? orders : [];
  return safeOrders.map(order => {
    const references = Array.isArray(order?.references) ? order.references : [];
    // Calcular totales
    let totalItems = 0;
    let totalQuantity = 0;
    let totalValue = 0;
    
    references.forEach(ref => {
      const items = Array.isArray(ref?.items) ? ref.items : [];
      items.forEach(item => {
        totalItems++;
        totalQuantity += item.quantity_pending;
        totalValue += item.subtotal_value;
      });
    });
    
    const computedSubtotal = totalValue;
    const subtotal = toFiniteNumber(order.document_subtotal_value, computedSubtotal);
    const discount = toFiniteNumber(order.document_discount_value, 0);
    const tax = toFiniteNumber(order.document_tax_value, 0);
    const total = toFiniteNumber(order.document_total_value, subtotal - discount + tax);

    return {
      id: order.document_number,
      orderNumber: order.document_number, // Número de pedido formateado
      document_number: order.document_number,
      purchase_order: order.purchase_order,
      client: order.client,
      client_name: order.client_name,
      client_nit: order.client || undefined,
      client_branch: order.client_branch || undefined,
      branch: order.client_branch || undefined,
      branch_name: order.client_branch || undefined,
      seller: order.seller,
      seller_name: order.seller_name,
      document_type: 'Pedido de Venta',
      document_date: order.document_date,
      creation_date: order.document_date,
      delivery_date: order.delivery_date,
      due_date: order.delivery_date,
      document_status: order.document_status,
      status: order.document_status,
      observations: order.observations || order.notes || undefined,
      notes: order.notes || order.observations || undefined,
      warehouse_name: order.warehouse_name || undefined,
      document_subtotal_value: subtotal,
      document_discount_value: discount,
      document_tax_value: tax,
      document_total_value: total,
      total_items: totalItems,
      total_quantity: totalQuantity,
      total_value: total,
      references
    };
  });
};

/**
 * Obtener el seller_id (cédula) del usuario actual desde el localStorage
 */
export function getCurrentUserSellerId(): string | null {
  try {
    if (typeof window === 'undefined') return null;

    const pickSellerId = (raw: any): string => {
      const candidates = [
        raw?.cedula,
        raw?.seller_id,
        raw?.sellerId,
        raw?.document,
        raw?.document_number,
        raw?.identification,
        raw?.user?.cedula,
        raw?.user?.seller_id,
        raw?.user?.sellerId,
        raw?.user?.document,
        raw?.user?.document_number,
        raw?.user?.identification,
      ];

      for (const candidate of candidates) {
        const normalized = String(candidate || '').trim();
        if (normalized) return normalized;
      }
      return '';
    };

    const sessionStr = localStorage.getItem('session');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      const fromSession = pickSellerId(session);
      if (fromSession) return fromSession;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      const fromUser = pickSellerId(user);
      if (fromUser) return fromUser;
    }

    return null;
  } catch (error) {
    console.error('[PendingOrdersService] Error getting user cedula:', error);
    return null;
  }
}

/**
 * Buscar un pedido específico por número de documento en SIESA.
 * Obtiene todos los pedidos del vendedor actual y filtra por document_number.
 * Si no se conoce el seller_id, busca sin filtro.
 * Retorna el PendingOrder con references completas, o null si no lo encuentra.
 */
export const fetchOrderDetailFromSiesa = async (
  documentNumber: string,
  sellerIdHint?: string | null,
): Promise<PendingOrder | null> => {
  if (!documentNumber) return null;
  try {
    const sessionSellerId = getCurrentUserSellerId();
    const sellerId = String(sellerIdHint || '').trim() || String(sessionSellerId || '').trim();
    const candidateSellerIds = Array.from(new Set([sellerId, '']));
    const candidates = buildDocumentCandidates(documentNumber, { fastPath: true });

    for (const candidateSellerId of candidateSellerIds) {
      for (const candidateDocument of candidates) {
        const order = await fetchOrderByDocumentFromSiesa(candidateDocument, candidateSellerId || undefined);
        if (order) return order;
      }
    }

    console.debug('[PendingOrdersService] No match found using get-order for', documentNumber);
    return null;
  } catch (err) {
    console.error('[PendingOrdersService] Error fetching order detail from SIESA:', err);
    return null;
  }
};

/**
 * Enriquecer un SimplifiedPendingOrder sin references buscándolo en SIESA.
 * Si lo encuentra, copia references y recalcula totales.
 */
export const enrichOrderWithSiesaDetail = async (
  order: SimplifiedPendingOrder,
  options?: { forceSync?: boolean; sellerIdHint?: string | null },
): Promise<SimplifiedPendingOrder> => {
  const forceSync = Boolean(options?.forceSync);
  const docNum = order.document_number || order.orderNumber || order.id;
  if (!docNum) return order;
  const siesaOrder = await fetchOrderDetailFromSiesa(docNum, options?.sellerIdHint);
  
  // Extraer order_type del document_number si no viene
  let order_type = order.order_type;
  if (!order_type && docNum) {
    const match = docNum.match(/^([A-Z]{3})-/i);
    if (match) order_type = match[1].toUpperCase();
  }
  
  if (!siesaOrder) return { ...order, order_type };

  const siesaReferences = Array.isArray(siesaOrder.references) ? siesaOrder.references : [];
  const localReferences = Array.isArray(order.references) ? order.references : [];
  const shouldUseSiesaReferences = siesaReferences.length > 0 && (forceSync || localReferences.length === 0);
  const references = shouldUseSiesaReferences ? siesaReferences : localReferences;

  if (!shouldUseSiesaReferences && localReferences.length > 0) {
    return {
      ...order,
      client_name: siesaOrder.client_name || order.client_name || order.client,
      seller: siesaOrder.seller || order.seller || '',
      seller_name: siesaOrder.seller_name || order.seller_name || '',
      purchase_order: siesaOrder.purchase_order || order.purchase_order || '',
      warehouse_name: siesaOrder.warehouse_name || order.warehouse_name || '',
      observations: siesaOrder.observations || siesaOrder.notes || order.observations || order.notes || '',
      notes: siesaOrder.notes || siesaOrder.observations || order.notes || order.observations || '',
      delivery_date: siesaOrder.delivery_date || order.delivery_date || '',
      document_date: siesaOrder.document_date || order.document_date || '',
      document_status: siesaOrder.document_status || order.document_status || order.status || '',
      status: siesaOrder.document_status || order.status || order.document_status || '',
    };
  }

  let totalItems = 0;
  let totalQuantity = 0;
  let totalValue = 0;
  references.forEach(ref => {
    const items = Array.isArray(ref?.items) ? ref.items : [];
    items.forEach(item => {
      totalItems++;
      totalQuantity += item.quantity_pending;
      totalValue += item.subtotal_value;
    });
  });

  const subtotal = toFiniteNumber(siesaOrder.document_subtotal_value, totalValue);
  const discount = toFiniteNumber(siesaOrder.document_discount_value, 0);
  const tax = toFiniteNumber(siesaOrder.document_tax_value, 0);
  const total = toFiniteNumber(siesaOrder.document_total_value, subtotal - discount + tax);

  return {
    ...order,
    references,
    total_items: totalItems,
    total_quantity: totalQuantity,
    document_subtotal_value: subtotal,
    document_discount_value: discount,
    document_tax_value: tax,
    document_total_value: total,
    total_value: total || order.total_value,
    // Enrich additional fields if missing
    order_type: order_type || siesaOrder.document_number?.match(/^([A-Z]{3})-/i)?.[1]?.toUpperCase(),
    client_name: siesaOrder.client_name || order.client_name || order.client,
    seller: siesaOrder.seller || order.seller || '',
    seller_name: siesaOrder.seller_name || order.seller_name || '',
    purchase_order: siesaOrder.purchase_order || order.purchase_order || '',
    warehouse_name: siesaOrder.warehouse_name || order.warehouse_name || '',
    observations: siesaOrder.observations || siesaOrder.notes || order.observations || order.notes || '',
    notes: siesaOrder.notes || siesaOrder.observations || order.notes || order.observations || '',
    delivery_date: siesaOrder.delivery_date || order.delivery_date || '',
    document_date: siesaOrder.document_date || order.document_date || '',
    document_status: siesaOrder.document_status || order.document_status || order.status || '',
    status: siesaOrder.document_status || order.status || order.document_status || '',
  };
};

export const enrichOrderReferencesWithSiesaDescriptions = async (
  order: SimplifiedPendingOrder,
): Promise<SimplifiedPendingOrder> => {
  const references = Array.isArray(order?.references) ? order.references : [];
  if (references.length === 0) return order;

  const referencesNeedingDescription = references.filter((reference) => {
    const referenceCode = String(reference?.reference || '').trim();
    if (!referenceCode) return false;

    const currentReferenceDescription = String(
      reference?.item_short_description || reference?.description || '',
    ).trim();
    const shouldRefreshReference = isPlaceholderDescription(referenceCode, currentReferenceDescription);

    const items = Array.isArray(reference?.items) ? reference.items : [];
    const shouldRefreshAnyItem = items.some((item) =>
      isPlaceholderDescription(
        referenceCode,
        item?.item_short_description || item?.item_description || item?.description || '',
      ),
    );

    return shouldRefreshReference || shouldRefreshAnyItem;
  });

  if (referencesNeedingDescription.length === 0) return order;

  const descriptions = new Map<string, string>();
  const referenceCodes = Array.from(
    new Set(
      referencesNeedingDescription
        .map((reference) => String(reference?.reference || '').trim())
        .filter(Boolean),
    ),
  );

  await runWithConcurrency(referenceCodes, 4, async (referenceCode) => {
    try {
      const matches = await withTimeoutFallback(
        searchSiesaItems({ reference: referenceCode, limit: 5 }),
        6000,
        () => [] as any[],
      );
      const match = Array.isArray(matches)
        ? matches.find((item: any) => String(item?.reference || '').trim() === referenceCode) || matches[0]
        : null;
      const description = String(match?.description || match?.name || '').trim();
      if (description) descriptions.set(referenceCode, description);
    } catch {
      // Keep existing values when APISIESA is unavailable.
    }
  });

  if (descriptions.size === 0) return order;

  return {
    ...order,
    references: references.map((reference) => {
      const referenceCode = String(reference?.reference || '').trim();
      const resolvedDescription = descriptions.get(referenceCode);
      if (!resolvedDescription) return reference;

      const currentReferenceDescription = String(
        reference?.item_short_description || reference?.description || '',
      ).trim();
      const shouldReplaceReferenceDescription = isPlaceholderDescription(
        referenceCode,
        currentReferenceDescription,
      );

      return {
        ...reference,
        description: shouldReplaceReferenceDescription ? resolvedDescription : reference.description,
        item_short_description: shouldReplaceReferenceDescription
          ? resolvedDescription
          : reference.item_short_description,
        items: (Array.isArray(reference?.items) ? reference.items : []).map((item) => {
          const currentItemDescription = String(
            item?.item_short_description || item?.item_description || item?.description || '',
          ).trim();
          const shouldReplaceItemDescription = isPlaceholderDescription(
            referenceCode,
            currentItemDescription,
          );

          if (!shouldReplaceItemDescription) return item;

          return {
            ...item,
            description: resolvedDescription,
            item_description: resolvedDescription,
            item_short_description: resolvedDescription,
          };
        }),
      };
    }),
  };
};


/**
 * Obtener pedidos pendientes del vendedor actual
 */
export const fetchCurrentUserPendingOrders = async (): Promise<SimplifiedPendingOrder[]> => {
  const sellerId = getCurrentUserSellerId();
  const orders = await fetchPendingOrders(sellerId || undefined);
  return transformToSimplified(orders);
};

/**
 * Formatear moneda colombiana
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value);
};

/**
 * Obtener estadísticas de pedidos pendientes
 */
export const getPendingOrdersStats = (orders: SimplifiedPendingOrder[]) => {
  const totalOrders = orders.length;
  const totalValue = orders.reduce((acc, order) => acc + order.total_value, 0);
  const totalItems = orders.reduce((acc, order) => acc + order.total_items, 0);
  
  const byStatus = orders.reduce((acc, order) => {
    const status = order.document_status || 'Sin estado';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const byClient = orders.reduce((acc, order) => {
    const client = order.client_name || 'Sin cliente';
    if (!acc[client]) {
      acc[client] = { count: 0, value: 0 };
    }
    acc[client].count++;
    acc[client].value += order.total_value;
    return acc;
  }, {} as Record<string, { count: number; value: number }>);
  
  return {
    totalOrders,
    totalValue,
    totalItems,
    byStatus,
    byClient
  };
};

export default {
  fetchPendingOrders,
  fetchCurrentUserPendingOrders,
  fetchOrderDetailFromSiesa,
  enrichOrderWithSiesaDetail,
  transformToSimplified,
  getCurrentUserSellerId,
  formatCurrency,
  getPendingOrdersStats
};
